import React, { useState } from "react";
import { ChevronLeft, Loader2, ChevronDown, ChevronUp, FileText, MapPin, Info, Calendar, Clock, CheckCircle, X, Upload, AlertCircle } from "lucide-react";
import { LabtestOrderDetails } from "../../../../../services/api/LaborderService/types";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";
import toast from "react-hot-toast";
import CancelOrderModal from "../CancelOrderModal";
import AddressSelectionModal from "../../../../common/AddressModal/AddressSelectionModal";

interface LabtestViewOrderDetailsProps {
  order: { orderId: number; orderNumber: string };
  orderDetails: LabtestOrderDetails;
  onBack: () => void;
}

const LabtestViewOrderDetails: React.FC<LabtestViewOrderDetailsProps> = ({ order, orderDetails, onBack }) => {
  const [showOrderDetails, setShowOrderDetails] = useState(true);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showReports, setShowReports] = useState(true);
  const [showBill, setShowBill] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const getStatusStyle = (status: string) => {
    if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
    const clean = status.toLowerCase();
    if (clean === "pending" || clean === "pending_admin" || clean === "admin_pending" || clean === "approval_pending") {
      return "text-amber-600 bg-amber-50 border border-amber-200";
    }
    if (clean === "cancelled" || clean === "rejected") {
      return "text-rose-600 bg-rose-50 border border-rose-200";
    }
    if (clean === "completed" || clean === "report_generated") {
      return "text-emerald-600 bg-emerald-50 border border-emerald-200";
    }
    return "text-blue-600 bg-blue-50 border border-blue-200";
  };

  const getStatusLabel = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isPDF = (url?: string) => url?.toLowerCase().includes(".pdf");

  const currentStatus = orderDetails.orderStatus?.toLowerCase() || "";
  const isStuckAtPayment = currentStatus === "payment_pending" || currentStatus === "pending";
  const isMovedToLab = currentStatus === "moved_to_lab";
  const isCancelled = currentStatus === "cancelled" || currentStatus === "rejected";
  const isCompleted = currentStatus === "completed" || currentStatus === "report_generated";

  const handleUploadReport = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one report file to upload.");
      return;
    }
    setIsUploading(true);
    try {
      const res = await LabService.updateLabOrder(order.orderId || orderDetails.id, selectedFiles);
      if (res.success) {
        toast.success("Lab report(s) uploaded successfully!");
        onBack();
      } else {
        toast.error(res.message || "Failed to upload report.");
      }
    } catch (error) {
      console.error("Error uploading reports:", error);
      toast.error("Failed to upload report documents.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden relative">
      <div className="flex-1 overflow-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer hover:text-slate-700"
          >
            <ChevronLeft size={16} /> Back to Labtest Orders
          </button>
          {!isCancelled && !isCompleted && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
            >
              <AlertCircle size={14} /> Cancel Order
            </button>
          )}
        </div>

        {/* Payment Pending Alert */}
        {isStuckAtPayment && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 shadow-sm">
            <Info className="text-amber-500 w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-xs font-extrabold text-amber-800 uppercase tracking-wide">Payment Pending</h4>
              <p className="text-[11px] text-amber-700 font-semibold mt-1">
                The patient must complete the online payment of ₹{orderDetails.orderTotal} to proceed.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left Column */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">

            {/* Patient & Clinic Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setShowOrderDetails(!showOrderDetails)}
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="uppercase tracking-wider">Patient & Clinic Details</span>
                  <span className={`px-2.5 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${getStatusStyle(orderDetails.orderStatus)}`}>
                    {getStatusLabel(orderDetails.orderStatus)}
                  </span>
                </div>
                {showOrderDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showOrderDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-semibold text-slate-600">
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Order ID</span>
                      <span className="text-slate-800 font-extrabold text-sm">#{orderDetails.orderNumber}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Order Date</span>
                      <span className="text-slate-800 font-bold">{formatDate(orderDetails.orderDate)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Ordered By</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {orderDetails.patientDetails?.patientProfileImage && (
                          <img
                            src={orderDetails.patientDetails.patientProfileImage}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover border border-slate-200"
                          />
                        )}
                        <div>
                          <div className="text-slate-800 font-bold">{orderDetails.patientDetails?.patientName}</div>
                          <div className="text-[0.6rem] text-slate-400 font-semibold">
                            +91 {orderDetails.patientPhoneNumber || orderDetails.patientDetails?.patientMobileNumber}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Clinic Details</span>
                      <span className="text-slate-800 font-bold block">{orderDetails.clinicDetails?.clinicName || "—"}</span>
                      {orderDetails.clinicDetails?.clinicMobileNumber && (
                        <span className="text-[0.6rem] text-slate-400 block font-semibold">
                          +91 {orderDetails.clinicDetails.clinicMobileNumber}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Address details */}
                  <div className="mt-4 pt-3 border-t border-slate-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">Sample Collection Address</span>
                      <button
                        onClick={() => setShowAddressModal(true)}
                        className="text-[0.6rem] text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500 rounded px-1.5 py-0.5 border border-emerald-200 font-bold cursor-pointer transition-all shadow-xs"
                      >
                        Edit Address
                      </button>
                    </div>
                    <div className="text-xs text-slate-700 flex items-start gap-1 font-semibold">
                      <MapPin size={13} className="text-rose-500 mt-0.5 flex-shrink-0" />
                      <span>
                        {orderDetails.orderAddress} (Pincode: {orderDetails.orderPincode})
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rider Details (If assigned) */}
            {orderDetails.deliveryPersonDetails && (
              <div className="bg-emerald-50/25 border border-emerald-100 rounded-xl p-4 flex items-center gap-3 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Pickup Rider Assigned</h4>
                  <p className="text-xs text-slate-800 font-extrabold mt-0.5">
                    {orderDetails.deliveryPersonDetails.deliveryPersonName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-semibold">
                    Phone: +91 {orderDetails.deliveryPersonDetails.deliveryPersonMobileNumber} • Vehicle: {orderDetails.deliveryPersonDetails.deliveryPersonVehicleNumber}
                  </p>
                </div>
              </div>
            )}

            {/* Booked Tests Table */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                Booked Labtests
              </h3>
              <div className="grid grid-cols-[3fr_2fr_1.5fr_1.5fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-50 pb-2 mb-2">
                <div>Test / Package Name</div>
                <div>Ordered For (Patient)</div>
                <div>Collection Date</div>
                <div className="text-right">Collection Slot</div>
              </div>
              <div className="space-y-2">
                {Array.isArray(orderDetails.labtestItemsDetails) ? (
                  orderDetails.labtestItemsDetails.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[3fr_2fr_1.5fr_1.5fr] gap-2 items-center py-2.5 px-2 rounded-lg border border-slate-50 bg-slate-50/30 hover:bg-slate-50 transition-colors"
                    >
                      <div className="text-xs font-bold text-slate-800">{item.labtestName || "Labtest"}</div>
                      <div className="flex items-center gap-2">
                        {item.bookedPatientProfileImage && (
                          <img
                            src={item.bookedPatientProfileImage}
                            alt=""
                            className="w-5 h-5 rounded-full object-cover border border-slate-200"
                          />
                        )}
                        <div>
                          <div className="text-xs font-bold text-slate-700">{item.bookedPatientName}</div>
                          {item.bookedPatientAge && (
                            <div className="text-[9px] text-slate-400 font-semibold">{item.bookedPatientAge}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 font-bold flex items-center gap-1">
                        <Calendar size={12} className="text-slate-400" />
                        {item.date ? new Date(item.date).toLocaleDateString("en-IN") : "—"}
                      </div>
                      <div className="text-xs text-slate-600 font-bold text-right flex items-center gap-1 justify-end">
                        <Clock size={12} className="text-slate-400" />
                        {item.timeSlot || "—"}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic">No labtests mapped for this order yet.</p>
                )}
              </div>
            </div>

            {/* Prescriptions (Optional) */}
            {((orderDetails.labtestPrescriptionDetails && orderDetails.labtestPrescriptionDetails.length > 0) || orderDetails.hasPrescription) && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowPrescriptions(!showPrescriptions)}
                >
                  <span className="uppercase tracking-wider">Patient Prescriptions</span>
                  {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showPrescriptions && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex flex-wrap gap-4">
                    {orderDetails.labtestPrescriptionDetails && orderDetails.labtestPrescriptionDetails.length > 0 ? (
                      orderDetails.labtestPrescriptionDetails.map((url, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl w-full">
                          <div className="flex items-center gap-2">
                            <FileText className="text-rose-500 w-6 h-6" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Prescription #{idx + 1}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setPdfPreviewUrl(url)}
                            className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border-none cursor-pointer"
                          >
                            View Document
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-100 rounded-xl w-full text-slate-500 text-xs font-semibold">
                        <Info size={16} className="text-amber-500" />
                        Prescription is required/uploaded, but no document link is available.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Order Invoice / Bill */}
            {orderDetails.billUrl && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowBill(!showBill)}
                >
                  <span className="uppercase tracking-wider">Order Invoice / Bill</span>
                  {showBill ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showBill && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex items-center justify-between bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      <FileText className="text-rose-500 w-8 h-8" />
                      <div>
                        <p className="text-xs font-bold text-slate-800">Invoice Bill PDF</p>
                        <p className="text-[10px] text-slate-400">Official tax receipt generated for Labtest</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPdfPreviewUrl(orderDetails.billUrl)}
                      className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all cursor-pointer border-none"
                    >
                      👁️ Preview Bill
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Uploaded Reports (If available) */}
            {orderDetails.labtestReportDetails && orderDetails.labtestReportDetails.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowReports(!showReports)}
                >
                  <span className="uppercase tracking-wider">Uploaded Test Reports</span>
                  {showReports ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showReports && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex flex-col gap-2">
                    {orderDetails.labtestReportDetails.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                        <div className="flex items-center gap-2">
                          <FileText className="text-emerald-500 w-6 h-6" />
                          <div>
                            <p className="text-xs font-bold text-slate-800">Uploaded Report Document #{idx + 1}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setPdfPreviewUrl(url)}
                          className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-100 transition-colors cursor-pointer"
                        >
                          Preview Report
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Upload Report Section (moved_to_lab) */}
            {isMovedToLab && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-2">
                    <FileText size={15} className="text-slate-400" />
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Lab Report Files</span>
                  </div>
                  {selectedFiles.length > 0 && (
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="text-[10px] font-bold text-emerald-600 border-none bg-transparent cursor-pointer hover:underline"
                    >
                      Manage Files
                    </button>
                  )}
                </div>
                <div className="p-5">
                  {selectedFiles.length > 0 ? (
                    <div className="space-y-2">
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <FileText size={16} className="text-emerald-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-700 truncate" title={file.name}>
                                {file.name}
                              </p>
                              <p className="text-[9px] text-slate-400 font-semibold">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
                            className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-55 bg-transparent border-none cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between py-3.5 text-center border border-dashed border-slate-200 rounded-xl px-4 bg-slate-50/20">
                      <div className="flex items-center gap-2">
                        <FileText className="text-slate-400" size={16} />
                        <span className="text-xs font-semibold text-slate-400">
                          No report files selected yet
                        </span>
                      </div>
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="px-4 py-2 text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all border-none cursor-pointer shadow-sm"
                      >
                        Select Report
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-[320px] flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col gap-4 sticky top-5">
              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Payment Breakdown</h4>

              <div className="flex flex-col gap-3.5 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>MRP Total</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.mrpTotal || orderDetails.orderTotal}</span>
                </div>
                <div className="flex justify-between text-emerald-600 bg-emerald-50/50 px-2 py-1.5 rounded-lg border border-emerald-100/50">
                  <span>Discount</span>
                  <span className="font-extrabold">-₹{orderDetails.paymentSummary?.discount || "0"}</span>
                </div>
                {orderDetails.paymentSummary?.coinsUsed > 0 && (
                  <div className="flex justify-between">
                    <span>Coins Applied</span>
                    <span className="text-slate-800 font-bold">-{orderDetails.paymentSummary.coinsUsed}</span>
                  </div>
                )}

                <hr className="border-slate-100 my-0.5" />

                <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span>Amount Paid</span>
                  <span className="text-lg font-black text-emerald-600">₹{orderDetails.orderTotal}</span>
                </div>

                <div className="mt-2 bg-slate-50/50 rounded-xl border border-slate-100 p-3 flex flex-col gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  <div className="flex justify-between">
                    <span>Payment Method</span>
                    <span className="text-slate-600 font-extrabold">{orderDetails.paymentMethod || "online"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Payment Status</span>
                    <span
                      className={`font-extrabold ${orderDetails.paymentStatus === "success"
                        ? "text-emerald-600"
                        : "text-amber-600"
                        }`}
                    >
                      {orderDetails.paymentStatus || "Pending"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions in Sidebar */}
              {isMovedToLab && (
                <div className="mt-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={handleUploadReport}
                    disabled={isUploading || selectedFiles.length === 0}
                    className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-xs font-bold transition-all border-none ${selectedFiles.length > 0 && !isUploading
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-200/50 hover:-translate-y-0.5 cursor-pointer"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                      }`}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Uploading...
                      </>
                    ) : (
                      <>
                        <FileText size={14} /> Upload Report & Complete
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document/Report PDF Preview Modal */}
      {pdfPreviewUrl && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300000] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setPdfPreviewUrl(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Document Preview</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setPdfPreviewUrl(null)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 bg-slate-100">
              {isPDF(pdfPreviewUrl) ? (
                <iframe
                  src={`${pdfPreviewUrl}#toolbar=0`}
                  className="w-full h-full border-none"
                  title="Document Preview PDF"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                  <img src={pdfPreviewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Report Modal */}
      {showUploadModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300000] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-[480px] w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Select Lab Report Files</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors border-none cursor-pointer"
                onClick={() => setShowUploadModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5 bg-white">
              {/* Drag & Drop Area */}
              <div
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const filesArray = Array.from(e.dataTransfer.files);
                    setSelectedFiles((prev) => [...prev, ...filesArray]);
                  }
                }}
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${dragActive
                  ? "border-emerald-500 bg-emerald-50/30"
                  : "border-slate-200 hover:border-slate-300 bg-slate-50/50"
                  }`}
                onClick={() => document.getElementById("report-file-input")?.click()}
              >
                <input
                  type="file"
                  id="report-file-input"
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const filesArray = Array.from(e.target.files);
                      setSelectedFiles((prev) => [...prev, ...filesArray]);
                    }
                  }}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                />
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mb-3">
                  <Upload size={20} />
                </div>
                <p className="text-xs font-bold text-slate-700">Drag & drop files here, or browse</p>
                <p className="text-[10px] text-slate-400 font-semibold mt-1">Supports PDF, PNG, JPG, JPEG, DOC</p>
              </div>

              {/* Selected Files List in modal */}
              {selectedFiles.length > 0 && (
                <div className="flex-1 flex flex-col min-h-0">
                  <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                    Selected Documents ({selectedFiles.length})
                  </h4>
                  <div className="space-y-2 overflow-y-auto max-h-[200px] pr-1">
                    {selectedFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FileText size={16} className="text-emerald-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-700 truncate" title={file.name}>
                              {file.name}
                            </p>
                            <p className="text-[9px] text-slate-400 font-semibold">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
                          className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors border-none bg-transparent cursor-pointer"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-slate-100 flex items-center justify-end bg-slate-50/30">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-5 py-2.5 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white border-none cursor-pointer shadow-sm transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        orderType="labtest"
        onSuccess={onBack}
      />
      {orderDetails && (
        <AddressSelectionModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          patientId={orderDetails.patientDetails?.patientId || ""}
          orderId={order.orderId}
          currentAddress={orderDetails.orderAddress}
          currentPincode={orderDetails.orderPincode}
          currentClinicId={orderDetails.clinicDetails?.clinicId}
          onAddressUpdated={onBack}
        />
      )}
    </div>
  );
};

export default LabtestViewOrderDetails;
